import { useCallback, useEffect, useMemo, useState } from 'react'
import { ownMass, segmentLength, type SegmentKey } from '@/domain/hs1'
import {
  PlmError,
  PlmHttpClient,
  plmCompare,
  rollup,
  type PlmAssembly,
  type PlmInfo,
  type PlmPart,
} from '@/domain/plm'
import { Alert, Badge, Button, Card, Table, Tabs, Td, Th } from '@/components/ui'
import { useSimulation } from '@/sim/SimulationProvider'
import { Metric, PageHead, num } from '@/sim/parts'

/**
 * PLM — was nach dem Konstruieren zurückkommt.
 *
 * DER RÜCKWEG SCHLIESST DIE KETTE. Bisher lief sie in eine Richtung: aus
 * Gelenkpunkten wurde eine Bewegung, aus der Bewegung ein Moment, aus dem
 * Moment ein Antrieb und aus dem Antrieb eine geschätzte Masse. Was die
 * Konstruktion daraus wirklich gemacht hat, stand nirgends.
 *
 * Diese Seite holt es: Teilenummer, Lage im Raum, Masse, Schwerpunkt,
 * Trägheitstensor, Werkstoff. Nicht die Form der Teile — die braucht kein
 * Rechenschritt.
 *
 * DIE SCHÄTZUNG BLEIBT DANEBEN STEHEN. Der Abgleich ist der Ertrag: er sagt,
 * wie weit die anthropometrische Annahme neben der Konstruktion liegt, und
 * damit, wie viel jede Zahl wert war, die auf ihr beruhte.
 */

const STATE_TONE: Record<PlmPart['state'], 'neutral' | 'warning' | 'success'> = {
  entwurf: 'warning',
  in_pruefung: 'neutral',
  freigegeben: 'success',
}

const STATE_LABEL: Record<PlmPart['state'], string> = {
  entwurf: 'Entwurf',
  in_pruefung: 'in Prüfung',
  freigegeben: 'freigegeben',
}

const ASSEMBLY = 'hs2-2026-08'

export function PlmPage() {
  const { base, model, plm, setPlm } = useSimulation()

  const client = useMemo(
    () => new PlmHttpClient({ baseUrl: import.meta.env.VITE_PLM_BASE_URL }),
    [],
  )

  const [info, setInfo] = useState<PlmInfo | null>(null)
  const [fetched, setFetched] = useState<PlmAssembly | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      setInfo(await client.info())
      setFetched(await client.assembly(ASSEMBLY))
    } catch (caught) {
      const reason =
        caught instanceof PlmError
          ? { unreachable: 'Die Gegenstelle antwortet nicht', notfound: 'Die Baugruppe gibt es dort nicht', invalid: 'Die Antwort ist unbrauchbar' }[caught.kind]
          : 'Unbekannter Fehler'
      setError(`${reason}: ${(caught as Error).message}`)
      setFetched(null)
    } finally {
      setBusy(false)
    }
  }, [client])

  /* Einmal beim Öffnen. Die Seite ist eine Abfrage und kein Abonnement — wer
     einen neuen Stand will, holt ihn. */
  useEffect(() => {
    void load()
  }, [load])

  /*
    Der Abgleich läuft IMMER gegen das Grundmodell, auch wenn die Baugruppe
    bereits übernommen ist. Gegen das gerechnete Modell verglichen stünde nach
    dem Übernehmen überall null — und die Auskunft, um die es hier geht, wäre
    verschwunden.
  */
  const deltas = useMemo(() => (fetched ? plmCompare(base, fetched) : []), [base, fetched])
  const entries = useMemo(() => (fetched ? rollup(base, fetched) : []), [base, fetched])

  const applied = plm !== null
  const measuredOwn = useMemo(() => {
    if (!fetched) return null
    const model2 = { ...base, masses: { ...base.masses } }
    for (const entry of rollup(base, fetched)) {
      model2.masses[entry.segment] = { ...model2.masses[entry.segment], kg: entry.mass }
    }
    return ownMass(model2)
  }, [base, fetched])

  const worst = deltas.reduce(
    (max, d) => (Math.abs(d.relative) > Math.abs(max?.relative ?? 0) ? d : max),
    null as (typeof deltas)[number] | null,
  )
  const draft = fetched?.parts.filter((p) => p.state !== 'freigegeben').length ?? 0

  return (
    <div className="stack">
      <PageHead
        title="PLM"
        actions={
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button size="sm" variant="ghost" onClick={() => void load()} disabled={busy}>
              {busy ? 'holt …' : 'Neu holen'}
            </Button>
            {applied ? (
              <Button size="sm" variant="secondary" onClick={() => setPlm(null)}>
                Zurück auf Schätzung
              </Button>
            ) : (
              <Button
                size="sm"
                variant="primary"
                disabled={!fetched}
                onClick={() => setPlm(fetched)}
              >
                Übernehmen
              </Button>
            )}
          </div>
        }
      />

      {info && (
        <Alert
          tone={info.simulated ? 'warning' : 'info'}
          title={info.simulated ? 'Simulierte Anbindung' : `Verbunden mit ${info.endpoint}`}
        >
          {info.simulated ? (
            <>
              Es hängt kein PLM daran. Der Client spricht echtes HTTP gegen{' '}
              <code>{info.endpoint}</code>, und dort liegt ein Datensatz aus dem Repository. Ein
              echtes PLM ist ein anderer Wert in <code>VITE_PLM_BASE_URL</code> und sonst nichts.
              Solange hier „simuliert" steht, gehört keine Zahl dieser Seite in eine Freigabe.
            </>
          ) : (
            <>Die Daten stammen aus der angeschlossenen Gegenstelle.</>
          )}
          {fetched && <> Stand: {fetched.source}</>}
        </Alert>
      )}

      {error && (
        <Alert tone="danger" title="Die Baugruppe konnte nicht geholt werden">
          {error}
        </Alert>
      )}

      {applied && (
        <Alert tone="accent" title="Die gemessenen Werte sind übernommen">
          Masse, Schwerpunkt und Trägheitsradius der gelieferten Segmente kommen ab jetzt aus dem
          PLM. Der geschätzte Stand bleibt daneben stehen; die Tabelle unten vergleicht weiter gegen
          ihn. Segmente ohne Teile behalten ihre Schätzung.
        </Alert>
      )}

      <div className="metrics">
        <Metric
          label="Teile"
          value={fetched?.parts.length ?? '—'}
          note={fetched ? `Revision ${fetched.revision}` : 'nicht geladen'}
        />
        <Metric
          label="Segmente"
          value={entries.length}
          note={`von ${Object.keys(base.masses).length - 1} im Modell`}
        />
        <Metric
          label="Eigenmasse gemessen"
          value={measuredOwn === null ? '—' : `${num(measuredOwn, 2)} kg`}
          note={`geschätzt ${num(ownMass(base), 2)} kg`}
          warn={measuredOwn !== null && Math.abs(measuredOwn - ownMass(base)) > 2}
        />
        <Metric
          label="Grösste Abweichung"
          value={worst ? `${worst.relative > 0 ? '+' : ''}${num(100 * worst.relative, 1)} %` : '—'}
          note={worst ? worst.label : ''}
          warn={worst !== null && Math.abs(worst.relative) > 0.1}
        />
        <Metric
          label="Nicht freigegeben"
          value={draft}
          note="Teile im Entwurf oder in Prüfung"
          warn={draft > 0}
        />
      </div>

      <Tabs
        aria-label="PLM"
        items={[
          { id: 'delta', label: 'Abgleich', content: <Compare deltas={deltas} /> },
          {
            id: 'segments',
            label: 'Ersatzkörper',
            content: <Segments entries={entries} model={model} />,
          },
          { id: 'parts', label: 'Teile', content: <Parts parts={fetched?.parts ?? []} /> },
        ]}
      />
    </div>
  )
}

/* -------------------------------------------------------------------------
   Abgleich
   ------------------------------------------------------------------------- */

function Compare({ deltas }: { deltas: ReturnType<typeof plmCompare> }) {
  if (deltas.length === 0) {
    return (
      <Card>
        <p className="page-lead">Keine Baugruppe geladen.</p>
      </Card>
    )
  }

  return (
    <Card flush title="Schätzung gegen Messung">
      <Table>
        <thead>
          <tr>
            <Th>Segment</Th>
            <Th numeric>Teile</Th>
            <Th numeric>geschätzt</Th>
            <Th numeric>gemessen</Th>
            <Th numeric>Δ</Th>
            <Th numeric>relativ</Th>
            <Th numeric>Schwerpunkt</Th>
            <Th numeric>Trägheitsradius</Th>
            <Th>Reifegrad</Th>
          </tr>
        </thead>
        <tbody>
          {deltas.map((d) => (
            <tr key={d.segment}>
              <Td>{d.label}</Td>
              <Td numeric>{d.parts}</Td>
              <Td numeric>{num(d.estimated.kg, 2)} kg</Td>
              <Td numeric>{num(d.measured.kg, 2)} kg</Td>
              <Td numeric className={Math.abs(d.relative) > 0.1 ? 'cell-warn' : undefined}>
                {d.deltaKg > 0 ? '+' : '−'}
                {num(Math.abs(d.deltaKg), 2)} kg
              </Td>
              <Td numeric className={Math.abs(d.relative) > 0.1 ? 'cell-warn' : undefined}>
                {d.relative > 0 ? '+' : '−'}
                {num(Math.abs(100 * d.relative), 1)} %
              </Td>
              <Td numeric>
                {num(d.estimated.com, 3)} → {num(d.measured.com, 3)}
              </Td>
              <Td numeric>
                {num(d.estimated.gyration, 3)} → {num(d.measured.gyration, 3)}
              </Td>
              <Td>
                <Badge tone={STATE_TONE[d.state]}>{STATE_LABEL[d.state]}</Badge>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  )
}

/* -------------------------------------------------------------------------
   Ersatzkörper
   ------------------------------------------------------------------------- */

function Segments({
  entries,
  model,
}: {
  entries: ReturnType<typeof rollup>
  model: ReturnType<typeof useSimulation>['model']
}) {
  if (entries.length === 0) {
    return (
      <Card>
        <p className="page-lead">Keine Baugruppe geladen.</p>
      </Card>
    )
  }

  return (
    <Card
      flush
      title="Ersatzkörper je Segment"
    >
      <Table>
        <thead>
          <tr>
            <Th>Segment</Th>
            <Th numeric>Masse</Th>
            <Th numeric>Schwerpunkt x · y · z</Th>
            <Th numeric>neben der Achse</Th>
            <Th numeric>Iyy</Th>
            <Th numeric>Ixx</Th>
            <Th numeric>Izz</Th>
            <Th numeric>Bezugslänge</Th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={`${e.segment}${e.side ?? ''}`}>
              <Td>
                {model.masses[e.segment].label}
                {e.side && <span className="cell-muted"> · {e.side}</span>}
              </Td>
              <Td numeric>{num(e.mass, 2)} kg</Td>
              <Td numeric>
                {num(e.cog.x, 0)} · {num(e.cog.y, 0)} · {num(e.cog.z, 0)}
              </Td>
              <Td numeric className={e.offAxis > 60 ? 'cell-warn' : undefined}>
                {num(e.offAxis, 0)} mm
              </Td>
              <Td numeric>{num(e.inertiaY, 4)}</Td>
              <Td numeric>
                <span className="cell-muted">{num(e.inertiaX, 4)}</span>
              </Td>
              <Td numeric>
                <span className="cell-muted">{num(e.inertiaZ, 4)}</span>
              </Td>
              <Td numeric>{num(1000 * segmentLength(model, e.segment as SegmentKey), 0)} mm</Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  )
}

/* -------------------------------------------------------------------------
   Teile
   ------------------------------------------------------------------------- */

function Parts({ parts }: { parts: PlmPart[] }) {
  if (parts.length === 0) {
    return (
      <Card>
        <p className="page-lead">Keine Teile geladen.</p>
      </Card>
    )
  }

  return (
    <Card flush title="Einzelteile" eyebrow={`${parts.length} Teile · Massen in kg, Lagen in mm`}>
      <Table>
        <thead>
          <tr>
            <Th>Nummer</Th>
            <Th>Benennung</Th>
            <Th>Segment</Th>
            <Th>Seite</Th>
            <Th numeric>Masse</Th>
            <Th numeric>Schwerpunkt</Th>
            <Th numeric>Iyy</Th>
            <Th>Werkstoff</Th>
            <Th>Stand</Th>
          </tr>
        </thead>
        <tbody>
          {parts.map((p) => (
            <tr key={`${p.number}-${p.side ?? ''}`}>
              <Td>
                <code>{p.number}</code>
                <span className="cell-muted"> {p.revision}</span>
              </Td>
              <Td>{p.name}</Td>
              <Td>
                <span className="cell-muted">{p.segment}</span>
              </Td>
              <Td>
                <span className="cell-muted">{p.side ?? 'mittig'}</span>
              </Td>
              <Td numeric>{num(p.mass, 3)}</Td>
              <Td numeric>
                {num(p.cog.x, 0)} · {num(p.cog.y, 0)} · {num(p.cog.z, 0)}
              </Td>
              <Td numeric>{num(p.inertia.yy, 4)}</Td>
              <Td>
                <span className="cell-muted">{p.material}</span>
              </Td>
              <Td>
                <Badge tone={STATE_TONE[p.state]}>{STATE_LABEL[p.state]}</Badge>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  )
}
